import React, { FunctionComponent } from 'react';
import { match } from 'react-router-dom';
import withStyles from 'react-jss';
import { useQuery } from '@apollo/react-hooks';
import { Grid } from 'semantic-ui-react';
import classnames from 'classnames';
import { pathOr, prop, isEmpty, isNil } from 'ramda';

import TopMetros from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/ConsumerActivity/components/TopMetros';
import { ClassProp } from 'helpers/types';
import { GET_MERCHANT_INFO } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview//MerchantOverview.queries';
import { useMerchantOverview } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import Query from 'components/Query';
import GET_THIRD_PARTY_LAPSED_ACTIVITY from 'components/LapsedActivity/LapsedActivity.queries';
import LapsedActivity, {
  LapsedUser,
} from 'components/LapsedActivity/components/LapsedActivity';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  match: match;
};

type QueryResponse = {
  loading: boolean;
  data: {
    lapsedUser: LapsedUser[];
  };
};

const styles = {
  section: {
    alignItems: 'flex-end !important',
    marginTop: '2rem !important',
  },
  lapsedActivity: {
    paddingLeft: '0 !important',
  },
  topMetros: {
    paddingRight: '0 !important',

    '&.lapsed': {
      padding: '0 !important',
    },
  },
};

const renderMerchantLandingPage = (
  { classes, match }: Props,
  isInternalUser: boolean,
  onlineOnly: boolean,
  isDemoUser: boolean,
) => (response: QueryResponse) => {
  const { data } = response;

  const lapsedUserData = prop('lapsedUser', data);
  const validData =
    !isEmpty(lapsedUserData) &&
    !isNil(lapsedUserData) &&
    (isInternalUser || isDemoUser);

  return (
    <Grid>
      <Grid.Row className={classes.section}>
        {validData && (
          <Grid.Column className={classes.lapsedActivity} width={6}>
            <LapsedActivity data={lapsedUserData} hasDownload />
          </Grid.Column>
        )}

        <Grid.Column
          width={!validData ? 16 : 10}
          className={classnames(classes.topMetros, {
            lapsed: !validData,
          })}
        >
          {(!onlineOnly || isDemoUser) && (
            <TopMetros match={match} fullWidth={!validData} />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

const MerchantLandingPage: FunctionComponent<Props> = (props) => {
  const { userProfile } = useMerchantOverview();
  const { isInternalUser, isDemoUser } = useUserMgmt();
  const merchantId = pathOr(
    userProfile.merchantId,
    ['params', 'merchantId'],
    props.match,
  ) as string;

  const { data: merchantInfo, loading } = useQuery(GET_MERCHANT_INFO, {
    variables: {
      params: {
        merchantId,
      },
    },
  });

  if (loading) return null;
  const onlineOnly = pathOr(
    [],
    ['merchantList', 'result', 0, 'onlineOnly'],
    merchantInfo,
  );

  return (
    <Query
      enableProgressInfo
      query={GET_THIRD_PARTY_LAPSED_ACTIVITY}
      render={renderMerchantLandingPage(
        props,
        isInternalUser,
        onlineOnly,
        isDemoUser,
      )}
      skip={!isInternalUser && !isDemoUser}
      variables={{
        params: {
          merchantId,
        },
      }}
    />
  );
};

export default withStyles(styles)(MerchantLandingPage);
